import React from 'react';
import cx from 'classnames';

import TaskTypeIcon from 'design-system/TaskTypeIcon';
import getTypeDisplayName from '@scale/shared/get-type-display-name';

interface ProductPillProps {
  type: string;
  isWorker?: boolean;
  showTitle?: boolean;
  compact?: boolean;
  disabled?: boolean;
}

const ProductPill: React.FC<ProductPillProps> = ({
  type,
  showTitle = true,
  isWorker,
  compact,
  disabled,
}) => {
  return (
    <>
      <div
        className={cx('pill', getTypeClassname(type), {
          'pill--with-title': showTitle,
          'pill--without-title': !showTitle,
          'pill-compact': compact,
        })}
      >
        <span className={cx('icon', { 'icon--with-title': showTitle })}>
          <TaskTypeIcon
            type={type}
            style={{ height: '24px', display: 'block' }}
            disabled={disabled}
          />
        </span>
        {showTitle && (
          <span className={cx('type-title')}>
            {getTypeDisplayName(type, { workerFacing: isWorker })}
          </span>
        )}
      </div>
      <style jsx>{`
        .pill {
          align-items: center;
          border-radius: 100px;
          display: inline-flex;
          font-size: 11px;
          letter-spacing: 0.5px;
        }
        .pill--with-title {
          padding-left: 2px;
          padding-right: 10px;
        }
        .pill--without-title {
          display: flex;
        }
        .pill-compact {
          width: 140px !important;
        }
        .sensor-fusion-cuboids,
        .lidarannotation {
          background: var(--color-sensor-fusion-cuboids);
        }
        .sensor-fusion-segmentation,
        .lidarsegmentation {
          background: var(--color-sensor-fusion-segmentation);
        }
        .semantic-segmentation,
        .segmentannotation {
          background: var(--color-semantic-segmentation);
        }
        .boxannotation,
        .annotation {
          background: var(--color-twod-boxes);
        }
        .polygonannotation {
          background: var(--color-polygons);
        }
        .cuboidannotation {
          background: var(--color-cuboids);
        }
        .lineannotation {
          background: var(--color-lines-splines);
        }
        .imageannotation {
          background: #ed72e2;
        }
        .videoboxannotation {
          background: var(--color-video-box-annotation);
        }
        .pointannotation {
          background: var(--color-point-annotation);
        }
        .categorization,
        .imagecategorization,
        .textcategorization {
          background: var(--color-categorization);
        }
        .comparison {
          background: rgb(218, 87, 200);
        }
        .datacollection {
          background: rgb(74, 122, 250);
        }
        .audiotranscription {
          background: rgb(255, 134, 110);
        }
        .transcription {
          background: #29d683;
        }
        .icon {
          min-width: 24px;
          width: 24px;
          height: 24px;
          display: inline-block;
          vertical-align: middle;
        }
        .icon--with-title {
          margin-right: 2px;
        }
        .type-title {
          color: #fff;
          font-weight: bold;
          text-transform: uppercase;
          display: inline-block;
          vertical-align: middle;
        }
      `}</style>
    </>
  );
};

// eslint-disable-next-line complexity
function getTypeClassname(type: string) {
  switch (type) {
    case 'sensor-fusion-cuboids':
    case 'sensor-fusion-segmentation':
    case 'semantic-segmentation':
    case 'segmentannotation':
    case 'lidarsegmentation':
    case 'lidarannotation':
    case 'polygonannotation':
    case 'boxannotation':
    case 'annotation':
    case 'cuboidannotation':
    case 'polygons':
    case 'lineannotation':
    case 'comparison':
    case 'categorization':
    case 'imagecategorization':
    case 'textcategorization':
    case 'datacollection':
    case 'audiotranscription':
    case 'transcription':
    case 'videoboxannotation':
    case 'pointannotation':
      return type;
    default:
      return 'imageannotation';
  }
}

export default ProductPill;
